/*
 * Kanban.tsx
 * 
 * Features:
 * - Two-column layout: left column for saved kanbans management, right column for board display
 * - Saved kanban configurations with database persistence
 * - URL-based board routing with /kanban/:boardId for shareable links
 * - Persists last used board ID in localStorage for quick access
 * - Separate search functionality for saved kanbans and board data
 * - KanbanConfig-based state management for live preview with editor
 * - Type and field selection moved to KanbanEditor panel
 * - Record filtering with FilterGroup support
 * - Lane visibility controls
 * - Responsive design with dark mode support
 * - Integration with SchemaContext for dynamic form fields
 * - Debounced search for board data filtering
 * - '+' button in header for quickly adding new kanban boards
 * 
 * Logic:
 * - Left column manages a list of saved kanban configurations stored in database
 * - Right column displays the kanban board with live preview from editor changes
 * - Configuration changes in editor immediately reflect in the board (controlled state)
 * - Save button persists current config to database
 * - Configurations are automatically loaded from database on component mount
 * - Click on saved kanban loads that configuration into the board
 * - '+' button resets to empty config and opens editor for new board creation
 * - Board ID in URL takes priority over localStorage when loading
 * 
 * Recent Changes:
 * - Integrates the board header directly with the shared page canvas
 * - Uses one semantic muted surface treatment for configured and empty board states
 * - Added kanban board ID to URL for shareable/bookmarkable links
 * - Persist last used board ID in localStorage
 * - Added '+' button beside gear button for adding new kanban boards
 * - Migrated from localStorage to database persistence
 * - Added schema-based access control
 * - Boards are now collaborative (shared by schema)
 * - Uses the shared page rhythm for the board header and board surface.
 */

import { FC, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { BREAKPOINTS } from '../constants/layoutConstants';
import { THEME_CLASSES } from '../constants/themeConstants';
import { foundationColorTokens } from '../foundation';
import { SchemaProvider, type Schema } from '../context/SchemaContext';
import { useSchemas } from '../context/SchemaContext';
import Spinner from '../components/common/Spinner';
import { useTeams } from '../context/TeamContext';
import PageLayout from '../components/patterns/PageLayout';
import KanbanBoard, { type KanbanBoardHandle } from '../components/kanban/KanbanBoard';
import { type FormField } from '../components/common/FormField';
import useDebounce from '../hooks/useDebounce';
import { useChatContext } from '../context/ChatContext';
import {
  type KanbanConfig,
  type KanbanBoard as KanbanBoardEntity,
  createEmptyConfig
} from '../models/kanban';
import {
  getKanbanBoards,
  createKanbanBoard,
  updateKanbanBoard,
  deleteKanbanBoard,
  parseKanbanConfig
} from '../services/kanbanService';

interface TypeOption {
  id: string;
  name: string;
  entityType: number;
  schema?: Schema;
}

interface TypeGroup {
  label: string;
  options: TypeOption[];
}

const LAST_KANBAN_BOARD_KEY = 'lastKanbanBoardId';
const KANBAN_BOARD_SURFACE_CLASSES = `h-full rounded-xl shadow-lg border ${foundationColorTokens.background.surfaceMuted} ${foundationColorTokens.border.subtle}`;

// Inner component that uses SchemaContext
const KanbanContent: FC = () => {
  const { boardId: urlBoardId } = useParams<{ boardId?: string }>();
  const navigate = useNavigate();
  const { isLoading: isLoadingUser } = useTeams();
  const { openEditor, updateEditorContent, activeTab, editorContent } = useChatContext();

  const [currentBoardId, setCurrentBoardId] = useState<number | null>(null);
  const hasInitialized = useRef(false);
  const kanbanBoardRef = useRef<KanbanBoardHandle>(null);

  // Main config state (source of truth for editor and board)
  const [config, setConfig] = useState<KanbanConfig>(createEmptyConfig());
  const [savedConfig, setSavedConfig] = useState<KanbanConfig | null>(null);

  // Derived state for backward compatibility
  const [selectedType, setSelectedType] = useState<TypeOption | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<Schema | null>(null);
  const [selectedField, setSelectedField] = useState<FormField | null>(null);
  const [schemaFields, setSchemaFields] = useState<FormField[]>([]);
  const [allSchemaFields, setAllSchemaFields] = useState<FormField[]>([]);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [savedKanbansSearchQuery, setSavedKanbansSearchQuery] = useState('');
  const [savedKanbans, setSavedKanbans] = useState<KanbanBoardEntity[]>([]);
  const [isLoadingBoards, setIsLoadingBoards] = useState(true);
  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(true);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }

    return window.matchMedia(BREAKPOINTS.desktop).matches;
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Get schemas for both accounts and contacts
  const { schemas: accountSchemas } = useSchemas(2); // 2 for accounts
  const { schemas: contactSchemas } = useSchemas(1); // 1 for contacts

  // Create grouped type options - memoized to prevent infinite re-renders
  const typeGroups: TypeGroup[] = useMemo(() => [
    {
      label: 'Accounts',
      options: accountSchemas.map(schema => ({
        id: `account-${schema.id}`,
        name: schema.name,
        entityType: 2,
        schema
      }))
    },
    {
      label: 'Contacts',
      options: contactSchemas.map(schema => ({
        id: `contact-${schema.id}`,
        name: schema.name,
        entityType: 1,
        schema
      }))
    }
  ], [accountSchemas, contactSchemas]);

  // Flatten all options for easier searching
  const allTypeOptions = typeGroups.flatMap(group => group.options);

  // Compute hasChanges by comparing config to savedConfig
  const hasChanges = useMemo(() => {
    if (!savedConfig) return config.name.trim() !== '' || config.typeId !== '';
    return JSON.stringify(config) !== JSON.stringify(savedConfig);
  }, [config, savedConfig]);

  // Update config handler for editor
  const updateConfig = useCallback((partial: Partial<KanbanConfig>) => {
    setConfig(prev => ({ ...prev, ...partial, updatedAt: new Date().toISOString() }));
  }, []);

  // Load saved kanbans from database
  const loadSavedKanbans = useCallback(async () => {
    try {
      setIsLoadingBoards(true);
      const boards = await getKanbanBoards();
      setSavedKanbans(boards);
    } catch (error) {
      console.error('Error loading saved kanbans:', error);
      setSavedKanbans([]);
    } finally {
      setIsLoadingBoards(false);
    }
  }, []);

  useEffect(() => {
    loadSavedKanbans();
  }, [loadSavedKanbans]);

  // Save current kanban configuration
  const saveCurrentKanban = useCallback(async () => {
    if (!config.name.trim() || !config.typeId || !config.groupField) {
      alert('Please enter a name and select type and field');
      return;
    }

    if (!selectedSchema) {
      alert('Please select a schema');
      return;
    }

    try {
      // Extract schemaId from typeId (format: "account-1" or "contact-1")
      const schemaId = parseInt(config.typeId.split('-')[1], 10);

      if (currentBoardId) {
        // Update existing board
        const updatedBoard = await updateKanbanBoard(currentBoardId, {
          name: config.name,
          config: config
        });
        const updatedConfig = parseKanbanConfig(updatedBoard);
        setSavedConfig(updatedConfig);
        setConfig(updatedConfig);
      } else {
        // Create new board
        const newBoard = await createKanbanBoard({
          schemaId,
          name: config.name,
          config: config
        });
        const newConfig = parseKanbanConfig(newBoard);
        setCurrentBoardId(newBoard.id);
        setSavedConfig(newConfig);
        setConfig(newConfig);
        // Update URL with new board ID and persist
        navigate(`/kanban/${newBoard.id}`, { replace: true });
        localStorage.setItem(LAST_KANBAN_BOARD_KEY, String(newBoard.id));
      }

      // Reload boards list
      await loadSavedKanbans();
    } catch (error) {
      console.error('Error saving kanban:', error);
      alert('Failed to save kanban board');
    }
  }, [config, selectedSchema, currentBoardId, loadSavedKanbans, navigate]);
  // Reset to saved config
  const handleReset = useCallback(() => {
    if (savedConfig) {
      setConfig(savedConfig);
    } else {
      setConfig(createEmptyConfig());
      setCurrentBoardId(null);
    }
  }, [savedConfig]);

  // Cancel and close editor
  const handleCancel = useCallback(() => {
    handleReset();
  }, [handleReset]);

  // Load a saved kanban board
  const loadSavedKanban = useCallback((board: KanbanBoardEntity, updateUrl = true) => {
    const kanbanConfig = parseKanbanConfig(board);
    setConfig(kanbanConfig);
    setSavedConfig(kanbanConfig);
    setCurrentBoardId(board.id);

    // Update URL with board ID
    if (updateUrl) {
      navigate(`/kanban/${board.id}`, { replace: true });
    }

    // Persist last used board ID
    localStorage.setItem(LAST_KANBAN_BOARD_KEY, String(board.id));

    // Find the type option to set derived state
    const typeOption = allTypeOptions.find(option => option.id === kanbanConfig.typeId);
    if (typeOption) {
      setSelectedType(typeOption);
      setSelectedSchema(typeOption.schema || null);
    }
  }, [allTypeOptions, navigate]);

  // Delete a saved kanban
  const deleteSavedKanban = useCallback(async (boardId: number) => {
    try {
      await deleteKanbanBoard(boardId);
      if (currentBoardId === boardId) {
        setConfig(createEmptyConfig());
        setSavedConfig(null);
        setCurrentBoardId(null);
        // Clear URL and localStorage when deleting current board
        navigate('/kanban', { replace: true });
        localStorage.removeItem(LAST_KANBAN_BOARD_KEY);
      }
      await loadSavedKanbans();
    } catch (error) {
      console.error('Error deleting kanban:', error);
      alert('Failed to delete kanban board');
    }
  }, [currentBoardId, loadSavedKanbans, navigate]);
  // Load board from URL param or localStorage on initial load
  useEffect(() => {
    if (hasInitialized.current || isLoadingBoards || savedKanbans.length === 0) return;
    hasInitialized.current = true;

    // Priority: URL param > localStorage
    const boardIdToLoad = urlBoardId || localStorage.getItem(LAST_KANBAN_BOARD_KEY);
    if (boardIdToLoad) {
      const boardId = parseInt(boardIdToLoad, 10);
      const board = savedKanbans.find(b => b.id === boardId);
      if (board) {
        loadSavedKanban(board, !urlBoardId); // Update URL only if loaded from localStorage
      } else if (urlBoardId) {
        // Board not found, clear invalid URL
        navigate('/kanban', { replace: true });
      }
    }
  }, [isLoadingBoards, savedKanbans, urlBoardId, loadSavedKanban, navigate]);

  // Parse schema fields when selected type changes
  useEffect(() => {
    if (selectedType && selectedSchema) {
      try {
        const fields: FormField[] = JSON.parse(selectedSchema.schema);
        // Store all fields for filtering
        setAllSchemaFields(fields);
        // Filter to only show fields with type "select" for grouping
        const selectFields = fields.filter(field => field.type === 'select');
        setSchemaFields(selectFields);

        // If config has a groupField, find and set it
        if (config.groupField) {
          const targetField = selectFields.find(field => field.name === config.groupField);
          if (targetField) {
            setSelectedField(targetField);
          }
        }
      } catch (error) {
        console.error('Error parsing schema:', error);
        setSchemaFields([]);
        setAllSchemaFields([]);
      }
    } else {
      setSchemaFields([]);
      setAllSchemaFields([]);
    }
  }, [selectedType, selectedSchema, config.groupField]);

  // Initialize with saved selections or first type option
  useEffect(() => {
    if (allTypeOptions.length > 0 && !selectedType) {
      // Filter to types matching current entityType
      const matchingTypes = allTypeOptions.filter(opt =>
        (config.entityType === 'accounts' && opt.entityType === 2) ||
        (config.entityType === 'contacts' && opt.entityType === 1)
      );

      if (matchingTypes.length === 0) return;

      const selectedOption = matchingTypes[0];
      setSelectedType(selectedOption);
      setSelectedSchema(selectedOption.schema || null);

      // Update config with initial type
      if (!config.typeId) {
        updateConfig({
          typeId: selectedOption.id,
          typeName: selectedOption.name,
          entityType: selectedOption.entityType === 2 ? 'accounts' : 'contacts'
        });
      }
    }
  }, [allTypeOptions, accountSchemas, contactSchemas, config.entityType, selectedType, config.typeId, updateConfig]);

  // Keep editor content in sync when config changes
  // Only update editor props when the editor is active to ensure live updates
  useEffect(() => {
    if (activeTab === 'editor' && editorContent?.type === 'kanban') {
      updateEditorContent({
        type: 'kanban',
        props: {
          config,
          savedConfig,
          accountSchemas,
          contactSchemas,
          typeGroups,
          schemaFields,
          allSchemaFields,
          selectedType,
          hasChanges,
          onConfigChange: updateConfig,
          onSave: saveCurrentKanban,
          onReset: handleReset,
          onCancel: handleCancel
        }
      });
    }
  }, [config, savedConfig, schemaFields, allSchemaFields, selectedType, hasChanges, activeTab, editorContent?.type, accountSchemas, contactSchemas, typeGroups, updateEditorContent, updateConfig, saveCurrentKanban, handleReset, handleCancel]);

  // Sync selectedType/selectedSchema when config.typeId changes
  useEffect(() => {
    if (config.typeId && allTypeOptions.length > 0) {
      const typeOption = allTypeOptions.find(opt => opt.id === config.typeId);
      if (typeOption && typeOption.id !== selectedType?.id) {
        setSelectedType(typeOption);
        setSelectedSchema(typeOption.schema || null);
      }
    } else if (!config.typeId) {
      // Clear derived state when type is cleared (e.g., when data source changes)
      setSelectedType(null);
      setSelectedSchema(null);
      setSelectedField(null);
    }
  }, [config.typeId, allTypeOptions, selectedType?.id]);

  // Sync selectedField when config.groupField changes
  useEffect(() => {
    if (config.groupField && schemaFields.length > 0) {
      const field = schemaFields.find(f => f.name === config.groupField);
      if (field && field.name !== selectedField?.name) {
        setSelectedField(field);
      }
    }
  }, [config.groupField, schemaFields, selectedField?.name]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(BREAKPOINTS.desktop);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  useEffect(() => {
    if (!isDesktopLayout) {
      setIsLeftColumnCollapsed(true);
    }
  }, [isDesktopLayout]);

  if (isLoadingUser) {
    return (
      <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
        <div className="flex items-center justify-center h-64">
          <Spinner />
          <span className="ml-3">Loading user data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      {/* Left Column - Search and Saved Kanbans */}
      <div className={`${isLeftColumnCollapsed ? 'w-12' : 'w-64'} bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-300 ease-in-out`}>
        {isLeftColumnCollapsed ? (
          /* Collapsed State - Only show expand button */
          <div className="p-2">
            <button
              onClick={() => setIsLeftColumnCollapsed(!isLeftColumnCollapsed)}
              className={`w-full p-2 rounded-lg ${THEME_CLASSES.hover.background} transition-colors flex items-center justify-center`}
              title="Expand sidebar"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 ${THEME_CLASSES.text.secondary} rotate-180`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            {/* Left Column Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className={`text-md font-semibold ${THEME_CLASSES.text.primary}`}>Saved Boards</h2>
                  </div>
                </div>
                <button
                  onClick={() => setIsLeftColumnCollapsed(!isLeftColumnCollapsed)}
                  className={`p-1 rounded-lg ${THEME_CLASSES.hover.background} transition-colors flex items-center justify-center`}
                  title="Collapse sidebar"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              </div>

              {/* Search Box for Saved Kanbans */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM2 8a6 6 0 1 1 10.89 3.476l4.817 4.817a1 1 0 0 1-1.414 1.414l-4.816-4.816A6 6 0 0 1 2 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search boards..."
                    value={savedKanbansSearchQuery}
                    onChange={(e) => setSavedKanbansSearchQuery(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const newConfig = createEmptyConfig();
                    setConfig(newConfig);
                    setSavedConfig(null);
                    setCurrentBoardId(null);
                    navigate('/kanban', { replace: true });
                    openEditor({
                      type: 'kanban',
                      props: {
                        config: newConfig,
                        savedConfig: null,
                        accountSchemas,
                        contactSchemas,
                        typeGroups,
                        schemaFields,
                        allSchemaFields,
                        selectedType: null,
                        hasChanges: false,
                        onConfigChange: updateConfig,
                        onSave: saveCurrentKanban,
                        onReset: handleReset,
                        onCancel: handleCancel
                      }
                    });
                  }}
                  className={`px-3 py-2 border rounded-md text-sm font-medium ${THEME_CLASSES.border} ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background} transition-colors`}
                  title="Create new board"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Saved Kanbans List */}
            <div className="flex-1 overflow-auto p-4">
              {isLoadingBoards ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner />
                </div>
              ) : savedKanbans.length === 0 ? (
                <div className="text-center py-8">
                  <div className={`text-gray-400 mb-2`}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className={`text-sm ${THEME_CLASSES.text.secondary}`}>No saved boards yet</p>
                  <p className={`text-xs ${THEME_CLASSES.text.secondary} mt-1`}>Create and save a board to see it here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {savedKanbans
                    .filter(board => board.name.toLowerCase().includes(savedKanbansSearchQuery.toLowerCase()))
                    .map((board) => {
                      const boardConfig = parseKanbanConfig(board);
                      return (
                        <div
                          key={board.id}
                          className={`p-3 rounded-lg border ${THEME_CLASSES.hover.background} cursor-pointer group transition-colors`}
                          onClick={() => loadSavedKanban(board)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className={`text-sm font-medium ${THEME_CLASSES.text.primary} truncate`}>
                                {board.name}
                              </h3>
                              <p className={`text-xs ${THEME_CLASSES.text.secondary} mt-1 truncate`}>
                                {board.schemaName || boardConfig.typeName} • {boardConfig.groupFieldLabel}
                              </p>
                              <p className={`text-xs ${THEME_CLASSES.text.secondary} mt-1`}>
                                {new Date(board.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm(`Delete "${board.name}"?${board.createdByName ? ' (Created by ' + board.createdByName + ')' : ''}`)) {
                                  deleteSavedKanban(board.id);
                                }
                              }}
                              className={`opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-opacity`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Right Column - Controls and Board */}
      <PageLayout className="min-w-0 flex-1">
        {/* Right Column Header with Controls */}
        <div data-testid="kanban-page-header">
          <div className="flex flex-col gap-4 px-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className={`text-xl font-bold my-0 ${THEME_CLASSES.text.primary}`}>
                {config.name || 'Kanban Board'}
              </h1>
              <button
                onClick={() => openEditor({
                  type: 'kanban',
                  props: {
                    config,
                    savedConfig,
                    accountSchemas,
                    contactSchemas,
                    typeGroups,
                    schemaFields,
                    allSchemaFields,
                    selectedType,
                    hasChanges,
                    onConfigChange: updateConfig,
                    onSave: saveCurrentKanban,
                    onReset: handleReset,
                    onCancel: handleCancel
                  }
                })}
                className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary} transition-colors`}
                title="Open Kanban Settings"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                onClick={() => {
                  const newConfig = createEmptyConfig();
                  setConfig(newConfig);
                  setSavedConfig(null);
                  setCurrentBoardId(null);
                  navigate('/kanban', { replace: true });
                  openEditor({
                    type: 'kanban',
                    props: {
                      config: newConfig,
                      savedConfig: null,
                      accountSchemas,
                      contactSchemas,
                      typeGroups,
                      schemaFields,
                      allSchemaFields,
                      selectedType: null,
                      hasChanges: false,
                      onConfigChange: updateConfig,
                      onSave: saveCurrentKanban,
                      onReset: handleReset,
                      onCancel: handleCancel
                    }
                  });
                }}
                className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary} transition-colors`}
                title="Add New Board"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <button
                onClick={() => {
                  if (kanbanBoardRef.current) {
                    kanbanBoardRef.current.exportBoard();
                  }
                }}
                className={`p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary} transition-colors ${!selectedType || !selectedField ? 'opacity-50 cursor-not-allowed' : ''}`}
                title="Export Board"
                disabled={!selectedType || !selectedField}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            </div>

            {/* Controls - Search and Info */}
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between lg:justify-end">
              {/* Current config info */}
              {selectedType && selectedField && (
                <div className={`text-xs ${THEME_CLASSES.text.secondary} flex flex-wrap items-center gap-2`}>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                    {selectedType.name}
                  </span>
                  <span>•</span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                    {selectedField.label || selectedField.name}
                  </span>
                </div>
              )}

              {/* Search Box for board data */}
              <div className="relative w-full sm:w-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM2 8a6 6 0 1 1 10.89 3.476l4.817 4.817a1 1 0 0 1-1.414 1.414l-4.816-4.816A6 6 0 0 1 2 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={`Search ${config.entityType === 'accounts' ? 'accounts' : 'contacts'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full sm:w-[200px] pl-10 pr-8 py-2 border rounded-md text-xs font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 ${searchQuery ? 'border-blue-500' : THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg className={`h-4 w-4 ${THEME_CLASSES.text.secondary} hover:${THEME_CLASSES.text.primary}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 1 1 1.414 0L10 8.586l4.293-4.293a1 1 0 1 1 1.414 1.414l-4.293 4.293 4.293 4.293a1 1 0 0 1-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 0 1-1.414-1.414l4.293-4.293-4.293-4.293a1 1 0 0 1 0-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Board Container */}
        <div className="flex-1 overflow-hidden px-6">
          {selectedType && selectedField && selectedSchema ? (
            <div
              className={`${KANBAN_BOARD_SURFACE_CLASSES} overflow-auto`}
              data-testid="kanban-board-surface"
            >
              <KanbanBoard
                ref={kanbanBoardRef}
                entityType={selectedType.entityType}
                schema={selectedSchema}
                field={selectedField}
                searchQuery={debouncedSearchQuery}
                visibleLanes={config.visibleLanes}
                includeUnassigned={config.includeUnassigned}
                filters={config.filters}
                boardName={config.name}
              />
            </div>
          ) : (
            <div
              className={`${KANBAN_BOARD_SURFACE_CLASSES} flex flex-col items-center justify-center`}
              data-testid="kanban-board-surface"
            >
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className={`text-lg font-semibold ${THEME_CLASSES.text.primary} mb-2`}>Configure Your Board</h3>
              <p className={`${THEME_CLASSES.text.secondary} text-center max-w-md mb-4`}>
                Click the settings icon to configure your Kanban board with data source, grouping field, and filters.
              </p>
              <button
                onClick={() => openEditor({
                  type: 'kanban',
                  props: {
                    config,
                    savedConfig,
                    accountSchemas,
                    contactSchemas,
                    typeGroups,
                    schemaFields,
                    allSchemaFields,
                    selectedType,
                    hasChanges,
                    onConfigChange: updateConfig,
                    onSave: saveCurrentKanban,
                    onReset: handleReset,
                    onCancel: handleCancel
                  }
                })}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Open Settings
              </button>
            </div>
          )}
        </div>
      </PageLayout>
    </div>
  );
};

const Kanban: FC = () => {
  return (
    <SchemaProvider>
      <KanbanContent />
    </SchemaProvider>
  );
};

export default Kanban;
